import { emitPdfPreview } from "./pdfPreviewBus.js";

/**
 * PDF_DELIVERY_MODE controla el comportamiento de deliverPdf:
 *   "in_app_modal"  - muestra el PDF en un modal embebido dentro de la app (recomendado).
 *   "preview_tab"   - abre una pestaña nueva con la URL blob (legacy).
 *   "download"      - descarga directa sin previsualizar.
 */
export const PDF_DELIVERY_MODE = "in_app_modal";
export const PDF_POPUP_BLOCKED_FALLBACK = "download";

const IN_APP_MODAL_MODE = "in_app_modal";
const PREVIEW_MODE = "preview_tab";
const DOWNLOAD_MODE = "download";

const BLOB_URL_REVOKE_DELAY_MS = 60000;

const isBrowser = () => typeof window !== "undefined" && typeof document !== "undefined";

const openNewTab = (url = "") => {
  const tab = window.open(url, "_blank");
  if (tab) {
    try {
      tab.opener = null;
    } catch (_) {
      // Ignore opener hardening errors.
    }
  }
  return tab;
};

const downloadBlobUrl = (blobUrl, fileName) => {
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const createPdfObjectUrl = (doc, fileName) => {
  const pdfBlob = doc.output("blob");

  if (typeof File !== "undefined") {
    try {
      const pdfFile = new File([pdfBlob], fileName, { type: "application/pdf" });
      return { url: URL.createObjectURL(pdfFile), blob: pdfFile };
    } catch (_) {
      // File not supported, fallback to plain Blob.
    }
  }

  return { url: URL.createObjectURL(pdfBlob), blob: pdfBlob };
};

/**
 * @deprecated No tiene efecto en modo "in_app_modal". Conservada por compatibilidad
 * con los callers existentes; devuelve null y puede eliminarse en una limpieza futura.
 *
 * En modo preview_tab (legacy) reserva una pestaña antes de una operación asíncrona
 * para evitar el bloqueo de pop-ups del navegador.
 * @returns {Window|null}
 */
export const reservePdfTarget = () => {
  if (!isBrowser() || PDF_DELIVERY_MODE !== PREVIEW_MODE) return null;

  try {
    return openNewTab("");
  } catch (_) {
    return null;
  }
};

/**
 * Entrega un PDF generado con jsPDF al usuario según el modo configurado.
 *
 * @param {{ doc: object, fileName: string, title?: string, targetWindow?: Window|null, forceDownload?: boolean, compartir?: object|null }} options
 *   - doc           jsPDF instance
 *   - fileName      nombre de archivo sugerido (con extensión .pdf)
 *   - title         título opcional para el modal (por defecto usa fileName)
 *   - targetWindow  pestaña reservada previamente (solo usado en modo preview_tab)
 *   - forceDownload si es true, siempre descarga directamente (ignora modo)
 * @returns {boolean} true si el PDF se entregó al usuario
 */
export const deliverPdf = ({ doc, fileName, title, targetWindow = null, forceDownload = false, compartir = null }) => {
  if (!doc || !fileName) return false;

  // Descarga directa forzada: garantiza el nombre de archivo independientemente del modo.
  if (!isBrowser() || PDF_DELIVERY_MODE === DOWNLOAD_MODE || forceDownload) {
    doc.save(fileName);
    // Cerrar la pestaña vacía que pudo haberse reservado en legacy.
    if (targetWindow && !targetWindow.closed) {
      try { targetWindow.close(); } catch (_) {}
    }
    return true;
  }

  // ─── Modo in_app_modal ───────────────────────────────────────────────────────
  if (PDF_DELIVERY_MODE === IN_APP_MODAL_MODE) {
    const pdfBlob = doc.output("blob");
    emitPdfPreview({ blob: pdfBlob, fileName, title: title || fileName, compartir: compartir || null });
    // Cerrar la pestaña vacía que pudo haberse reservado en legacy.
    if (targetWindow && !targetWindow.closed) {
      try { targetWindow.close(); } catch (_) {}
    }
    return true;
  }

  // ─── Modo preview_tab (legacy) ───────────────────────────────────────────────
  const { url: pdfUrl } = createPdfObjectUrl(doc, fileName);
  let previewOpened = false;

  if (targetWindow && !targetWindow.closed) {
    try {
      targetWindow.location.href = pdfUrl;
      previewOpened = true;
    } catch (_) {
      previewOpened = false;
    }
  }

  if (!previewOpened) {
    try {
      const newTab = openNewTab(pdfUrl);
      previewOpened = Boolean(newTab);
    } catch (_) {
      previewOpened = false;
    }
  }

  if (!previewOpened && PDF_POPUP_BLOCKED_FALLBACK === DOWNLOAD_MODE) {
    downloadBlobUrl(pdfUrl, fileName);
  }

  setTimeout(() => URL.revokeObjectURL(pdfUrl), BLOB_URL_REVOKE_DELAY_MS);
  return previewOpened;
};
